/**
 * The region panel type and the settings it draws from.
 */

const region = {
  names: [
    {
      id: 'region',
      label: 'Region map',
      phrase: 'a region map',
      guide: 'region.md',
      advanced: true,
      toolSummary: 'a value shaded across countries — one row per country, keyed by ISO code or country name',
      promptSummary: 'how a value compares across countries, shaded light to dark — one row per country, keyed by an ISO 3166-1 code or a country name; it cannot shade states, provinces, or any area smaller than a country'
    }
  ],
  block: 'region',
  /* Region map. One row is one country, so the config is the code column and the number that
     shades it. There is no colour field: the fill is a scale rather than a category, so a
     per-country colour would be the reader inventing a legend the data does not have. */
  fields: {
    regionKey: { kind: 'string', required: true, hint: 'a shaded country needs a code or a name to match on', label: 'Country' },
    valueKey: { kind: 'string', required: true, hint: 'a shaded country needs a number to shade by', label: 'Value' },
    /* Same choice, same words as the marker map: globe suits world-spanning data, flat suits a
       region of it. A choropleth is more often read continent by continent, so it opens flat. */
    projection: { kind: 'enum', values: ['globe', 'flat'], default: 'flat', label: 'Projection', ariaLabel: 'How the earth is drawn', valueLabels: { globe: 'Globe', flat: 'Flat' } }
  },
  editorHint: 'One row is one country, matched on an ISO 3166-1 code (US, USA) or a country name (United States). Aggregate to one row per country in SQL: a repeat is counted as not shown rather than added up.',
  /* Which column carries the code is the first thing the assistant needs, for the same reason the
     marker map leads with its coordinates: neither panel can derive it from a country's name. */
  stateSummary: ({ block }) => (block.regionKey && block.valueKey ? `region: ${block.regionKey} × ${block.valueKey}` : ''),
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: ['regionKey', 'valueKey'],
    description: 'Region map mapping (displayType "region"): ONE ROW IS ONE COUNTRY, shaded light to dark by its value. The country column may hold an ISO 3166-1 code ("US", "USA") or a country name ("United States") — all three are matched, so do NOT convert between them first. COUNTRIES ONLY: it cannot shade states, provinces, counties, or postcodes, so an ask by state is a bar chart and you should say so. Aggregate to one row per country in the query; a repeat is counted as not shown. Read immutable/analytics/panel-types/region.md before configuring one.',
    properties: {
      regionKey: { type: 'string', description: 'Column identifying the country: an ISO 3166-1 alpha-2 code ("US", "GB"), an alpha-3 code ("USA", "GBR"), or an English country name ("United States"). Case and punctuation do not matter. Dot paths work. A value that is not a country ("EMEA", "APAC") simply stays unshaded.' },
      valueKey: { type: 'string', description: 'Column holding the number the country is shaded by. Dot paths work. Zero is a real value and gets the lightest shade; a country absent from the rows is drawn in the separate "no data" grey instead.' },
      projection: { type: 'string', enum: ['globe', 'flat'], description: '"flat" (default) is the familiar flat map and the better choice for most comparisons, including a single continent. "globe" draws the earth as a sphere, which suits genuinely worldwide data but hides half the countries at any moment.' }
    }
  }
};

export default region;
